/**
 * Board-scoped PERFORMANCE / GOALKEEPER metric scores for the pipeline
 * filter panel (SE-6871).
 *
 * The board's card query cannot carry these — it already measures ~1787 of
 * the 2000-point SE-5063 complexity cap — so they ride a dedicated sidecar
 * read: backend-v2's `listKanbanBoardPlayerMetrics(kanbanBoardId)`, one flat
 * row per carded player, joined client-side by playerId.
 * @module features/player-kanban/hooks/useBoardPlayerMetrics
 */

import { useMemo } from "react";

import { useListKanbanBoardPlayerMetricsQuery } from "@/generated/graphql";

import type { BoardPlayerMetrics } from "../utils/playerFilter";

/** Result of {@link useBoardPlayerMetrics}. */
interface UseBoardPlayerMetricsResult {
  /** Metric scores by player id, or `undefined` while unavailable. */
  readonly metricsById: ReadonlyMap<string, BoardPlayerMetrics> | undefined;
  /**
   * Whether the metrics have loaded — gates the PERFORMANCE / GOALKEEPER
   * filter sections so they can never render data-less.
   */
  readonly hasMetrics: boolean;
}

/**
 * Read the active board's per-player metric scores.
 * @param kanbanBoardId - The active board; the query is skipped until known.
 * @returns Metric scores by player id and their availability.
 */
const useBoardPlayerMetrics = (
  kanbanBoardId: string | null | undefined
): UseBoardPlayerMetricsResult => {
  const { data } = useListKanbanBoardPlayerMetricsQuery({
    variables: { kanbanBoardId: kanbanBoardId ?? "" },
    skip: !kanbanBoardId,
  });

  const metricsById = useMemo(() => {
    const rows = data?.listKanbanBoardPlayerMetrics;
    if (!rows) return undefined;
    return new Map<string, BoardPlayerMetrics>(
      rows.map(row => [
        row.playerId,
        {
          carrying: row.carryingTransGlobalCategoricalScore,
          dribbling: row.dribblingTransGlobalCategoricalScore,
          passing: row.passingTransGlobalCategoricalScore,
          shooting: row.shotLocationTransGlobalCategoricalScore,
          gkPassing: row.gkPassingTransCategoricalScore,
          gkShotStopping: row.gkShotStoppingTransCategoricalScore,
          gkPositioning: row.gkPositioningTransCategoricalScore,
          fitScore: row.fitScore,
          teamStyleFit: row.teamStyleFit,
        },
      ])
    );
  }, [data]);

  return { metricsById, hasMetrics: metricsById !== undefined };
};

export default useBoardPlayerMetrics;
